import { useEffect, useState } from "react";
import { api, openDocument, formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { FileText, Pencil } from "lucide-react";

const rp = (n) => `Rp ${Math.round(n || 0).toLocaleString("id-ID")}`;
const STATUS = { draft: "Draft", sent: "Terkirim ke supplier", received: "Barang diterima" };
const FIELDS = [["supplier_name", "Nama Supplier"], ["supplier_address", "Alamat Supplier"], ["supplier_phone", "Telepon Supplier"], ["payment_method", "Payment Method"], ["subject", "Hal"]];

function EditDialog({ po, onClose, onSaved }) {
  const [f, setF] = useState(po);
  const save = async () => {
    try {
      const { data } = await api.put(`/purchase-orders/${po.id}`, Object.fromEntries([...FIELDS.map(([k]) => [k, f[k] || ""]), ["status", f.status]]));
      toast.success("PO disimpan"); onSaved(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Gagal menyimpan"); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="po-edit-dialog">
        <DialogHeader><DialogTitle>PO {po.po_number}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {FIELDS.map(([k, l]) => (
            <div key={k}><Label className="text-xs">{l}</Label>
              <Input value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`po-field-${k}`} /></div>
          ))}
          <div><Label className="text-xs">Status</Label>
            <select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} data-testid="po-field-status" className="w-full border border-border rounded-md h-9 px-2 text-sm bg-background">
              {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select></div>
          <Button onClick={save} className="w-full" data-testid="po-save-btn">Simpan</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function PurchaseOrders() {
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null);
  useEffect(() => { api.get("/purchase-orders").then((r) => setRows(r.data)).catch(() => toast.error("Gagal memuat PO")); }, []);
  return (
    <div data-testid="purchase-orders-page">
      <PageHeader overline="Procurement · Sinta" title="PO Pembelian" />
      <div className="p-8 space-y-4">
      <p className="text-sm text-muted-foreground">Draft PO ke supplier dibuat Sinta setelah customer setuju. Lengkapi data supplier, unduh PDF, lalu kirim manual.</p>
      <div className="border border-border bg-card rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-xs text-muted-foreground"><tr>
            {["No. PO", "Tanggal", "Customer / Penawaran", "Supplier", "Total", "Status", ""].map((h) => <th key={h} className="text-left px-4 py-2 font-medium">{h}</th>)}
          </tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground" data-testid="po-empty">Belum ada PO pembelian.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-border" data-testid="po-row">
                <td className="px-4 py-2 font-mono">{r.po_number}</td>
                <td className="px-4 py-2">{(r.created_at || "").slice(0, 10)}</td>
                <td className="px-4 py-2">{r.customer}<div className="text-xs text-muted-foreground">{r.quote_number} · {r.case_number}</div></td>
                <td className="px-4 py-2">{r.supplier_name || <span className="text-amber-600">Belum diisi</span>}</td>
                <td className="px-4 py-2 font-mono">{rp(r.total)}</td>
                <td className="px-4 py-2">{STATUS[r.status] || r.status}</td>
                <td className="px-4 py-2 flex gap-2 justify-end">
                  <Button size="sm" variant="outline" onClick={() => setEdit(r)} data-testid="po-edit-btn"><Pencil className="w-3.5 h-3.5" /></Button>
                  <Button size="sm" variant="outline" onClick={() => openDocument(`/purchase-orders/${r.id}/pdf`, `PO-${r.po_number}.pdf`).catch(() => toast.error("Gagal membuka PDF"))} data-testid="po-pdf-btn"><FileText className="w-3.5 h-3.5 mr-1" /> PDF</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </div>
      {edit && <EditDialog po={edit} onClose={() => setEdit(null)} onSaved={(d) => { setRows(rows.map((x) => (x.id === d.id ? d : x))); setEdit(null); }} />}
    </div>
  );
}
